import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import User from '@/models/User'
import { syncSentryUser } from '@/utils/sentryBootstrap'
import { authService, normalizeApiError, API_ERROR_GROUPS, restaurantService } from '@/services'
import { clearSessionToken, setSessionToken } from '@/auth/session'
import { useToastStore } from '@/stores/toast'
import { useOwnerRestaurantsStore } from '@/stores/ownerRestaurants'
import { useBreadcrumbStore } from '@/stores/breadcrumb'
import { usePaidFeatureStore } from '@/stores/paidFeature'
import { usePageFabStore } from '@/stores/pageFab'
import { useReservationsHowItWorksStore } from '@/stores/reservationsHowItWorks'

export const useAppStore = defineStore('app', () => {
  const user = ref(null)

  watch(
    user,
    (u) => {
      syncSentryUser(u)
    },
    { immediate: true },
  )

  const authBootstrapStatus = ref('idle') // idle | loading | done
  const authBootstrapHadNetworkError = ref(false)
  let authBootstrapPromise = null

  function clearLegacyAuthStorage() {
    try {
      localStorage.removeItem('rms-auth')
      localStorage.removeItem('rms-auth-token')
      localStorage.removeItem('rms-user-id')
      localStorage.removeItem('rms-user-name')
      localStorage.removeItem('rms-user-email')
      localStorage.removeItem('rms-user-verified')
      localStorage.removeItem('rms-user-pending-email')
      localStorage.removeItem('rms-user-is-paid')
      localStorage.removeItem('rms-user-is-superadmin')
      localStorage.removeItem('rms-user-is-active')
      // Legacy global wizard flag; per-user keys live in firstRestaurantWizardStorage.js
      localStorage.removeItem('rms.first_restaurant_wizard_dismissed')
    } catch {
      // ignore (e.g. SSR / privacy mode)
    }
  }

  const isAuthenticated = computed(() => !!user.value)
  const isAuthBootstrapping = computed(() => authBootstrapStatus.value === 'loading')
  const isAuthBootstrapped = computed(() => authBootstrapStatus.value === 'done')

  /**
   * Set user from a plain payload (e.g. after login/register before token is stored).
   * Prefer setUserFromApi(data) when you have an API user response; this is kept for legacy/custom flows.
   */
  function login(payload = {}) {
    const data = {
      uuid: payload.uuid ?? payload.id ?? user.value?.id,
      name: payload.name ?? user.value?.name ?? 'Restaurant Owner',
      email: payload.email ?? user.value?.email ?? 'owner@example.com',
      email_verified_at: payload.email_verified_at ?? (payload.emailVerifiedAt !== undefined ? payload.emailVerifiedAt : user.value?.emailVerifiedAt ?? null),
      is_paid: payload.is_paid,
      plan_slug: payload.plan_slug ?? payload.planSlug,
      feature_keys: payload.feature_keys ?? payload.featureKeys,
      subscription_ends_at: payload.subscription_ends_at ?? payload.subscriptionEndsAt,
      is_superadmin: payload.is_superadmin,
      is_active: payload.is_active,
      vip: payload.vip,
      can_change_password: payload.can_change_password,
      first_login_tour_completed_at: payload.first_login_tour_completed_at ?? user.value?.firstLoginTourCompletedAt ?? null,
    }
    user.value = User.fromApi(data)
  }

  async function logout() {
    try {
      await authService.logout()
    } finally {
      clearAuthState()
    }
  }

  /** Clear token and user only (no API call). Use from API interceptor on 401/403 to avoid redirect loops. */
  function clearAuthState() {
    user.value = null
    clearSessionToken()
    clearLegacyAuthStorage()
    try {
      restaurantService.clearAuthenticatedCaches()
    } catch {
      // ignore
    }
    try {
      usePaidFeatureStore().close()
      usePageFabStore().setPageFab(false)
      useReservationsHowItWorksStore().close()
    } catch {
      // ignore
    }
    try {
      useOwnerRestaurantsStore().reset()
      useBreadcrumbStore().clearRestaurant()
    } catch {
      // Stores may be unavailable (e.g. before Pinia is mounted)
    }
  }

  /** Set user from API response (e.g. after auth fetch). */
  function setUserFromApi(data) {
    user.value = User.fromApi(data)
  }

  /**
   * Apply an auth response that includes { user, token, token_type }.
   * Token is stored in memory only.
   */
  function applyAuthResponse(data) {
    if (data?.token) setSessionToken(data.token, data.token_type)
    if (data?.user) {
      // Clear all owner-scoped service caches on session change so another account never sees the previous user's data.
      try {
        restaurantService.clearAuthenticatedCaches()
      } catch {
        /* ignore */
      }
      setUserFromApi(data.user)
    }
  }

  /**
   * Attempt to bootstrap auth once per page load:
   * - Calls POST /auth/refresh (uses HttpOnly cookie)
   * - On success: sets in-memory token + user
   * - On failure (401/403): clears state, no redirect here (guards decide)
   */
  async function bootstrapAuth() {
    if (isAuthBootstrapped.value) return
    if (authBootstrapPromise) return authBootstrapPromise

    authBootstrapStatus.value = 'loading'
    authBootstrapPromise = (async () => {
      clearLegacyAuthStorage()
      try {
        // Reasonable timeout so bootstrap doesn't hang when API is unreachable; 10s allows slow networks to complete.
        const data = await authService.refresh({ timeout: 10000 })
        applyAuthResponse(data)
        authBootstrapHadNetworkError.value = false
      } catch (e) {
        const normalized = normalizeApiError(e)
        if (normalized.group === API_ERROR_GROUPS.NETWORK) {
          authBootstrapHadNetworkError.value = true
          // Offline / network error: do not present as "logged out".
          // Keep any existing in-memory auth state (e.g. user just logged in) and let the UI render.
          try {
            const toast = useToastStore()
            toast.info('You appear offline. Some features may not work until you reconnect.')
          } catch {
            // ignore (e.g. Pinia not ready)
          }
        } else {
          authBootstrapHadNetworkError.value = false
          // If a user just logged in while refresh-on-boot was in-flight, do not clear the fresh in-memory session.
          // Only clear when we still have no authenticated user.
          if (!user.value) clearAuthState()
        }
      } finally {
        authBootstrapStatus.value = 'done'
        authBootstrapPromise = null
      }
    })()

    return authBootstrapPromise
  }

  return {
    user,
    isAuthenticated,
    isAuthBootstrapping,
    isAuthBootstrapped,
    authBootstrapHadNetworkError,
    login,
    logout,
    clearAuthState,
    setUserFromApi,
    applyAuthResponse,
    bootstrapAuth,
  }
})
